/** DocumentExtract 解析进度的展示文案（纯函数，便于单测）。 */
export type DocumentExtractProgressView = {
  stage: 'parse' | 'ocr' | 'visual' | 'save' | 'done';
  pagesDone: number;
  totalPages: number;
  regionsDone?: number;
  regionsTotal?: number;
  extractId?: string;
  fileName?: string;
};

type Locale = 'zh' | 'zhTw' | 'en' | string;

const TEXT = {
  zh: { parse: '正在解析文档', ocr: '正在识别扫描页', visual: '正在云端校正', save: '正在保存 Markdown', done: '解析完成', page: (d: number, t: number) => `第 ${d}/${t} 页`, region: (d: number, t: number) => `${d}/${t} 处`, total: (t: number) => `共 ${t} 页` },
  zhTw: { parse: '正在解析文件', ocr: '正在辨識掃描頁', visual: '正在雲端校正', save: '正在儲存 Markdown', done: '解析完成', page: (d: number, t: number) => `第 ${d}/${t} 頁`, region: (d: number, t: number) => `${d}/${t} 處`, total: (t: number) => `共 ${t} 頁` },
  en: { parse: 'Parsing document', ocr: 'Recognizing scanned pages', visual: 'Correcting in the cloud', save: 'Saving Markdown', done: 'Extraction complete', page: (d: number, t: number) => `${d}/${t}`, region: (d: number, t: number) => `${d}/${t} regions`, total: (t: number) => `${t} pages` },
};

const ratio = (done: number, total: number): number | null => (
  total > 0 ? Math.max(0, Math.min(100, Math.round((done / total) * 100))) : null
);

export function formatDocumentExtractProgress(
  progress: DocumentExtractProgressView,
  locale: Locale,
): { label: string; percent: number | null } {
  const t = locale === 'en' ? TEXT.en : (locale === 'zh-TW' || locale === 'zhTw') ? TEXT.zhTw : TEXT.zh;
  const { stage, pagesDone, totalPages } = progress;
  if (stage === 'save') return { label: t.save, percent: 100 };
  if (stage === 'done') return { label: totalPages > 0 ? `${t.done} · ${t.total(totalPages)}` : t.done, percent: 100 };
  if (stage === 'visual' && typeof progress.regionsDone === 'number' && typeof progress.regionsTotal === 'number' && progress.regionsTotal > 0) {
    return { label: `${t.visual} · ${t.region(progress.regionsDone, progress.regionsTotal)}`, percent: ratio(progress.regionsDone, progress.regionsTotal) };
  }
  const base = t[stage] ?? t.parse;
  if (totalPages <= 0) return { label: base, percent: null };
  return { label: `${base} · ${t.page(pagesDone, totalPages)}`, percent: ratio(pagesDone, totalPages) };
}

/** DocumentExtract 及其历史隐藏别名（旧会话里的工具名）。 */
const DOCUMENT_EXTRACT_TOOL_NAMES = new Set([
  'DocumentExtract', 'PdfExtract', 'DocumentParse', 'DocumentRead', 'DocumentValidate', 'DocumentVisualCorrect',
]);

export function isDocumentExtractToolName(name: string): boolean {
  return DOCUMENT_EXTRACT_TOOL_NAMES.has(name);
}

const baseName = (value: string): string => value.split(/[\\/]/).pop() ?? value;

/**
 * 运行中的 DocumentExtract 卡片展示哪条进度：本调用自己的事件优先；否则仅当会话最近一条未完成事件
 * 属于**同一文件**（事件 fileName 与本调用 input.path 的文件名一致）时沿用（续读调用等下一批）；
 * 其余情况（别的文档、旧 SDK 事件无文件身份、附件 inputRef）给不确定态「正在解析文档」——
 * 不能把上一份文档的页数挂到新调用上。SDK 会在拿到总页数后立即推送本调用自己的事件。
 */
export function resolveDocumentExtractProgress<T extends DocumentExtractProgressView>(
  own: T | undefined,
  sessionLatest: T | undefined,
  input?: unknown,
): T | DocumentExtractProgressView {
  if (own) return own;
  const inputPath = input && typeof input === 'object' ? (input as { path?: unknown }).path : undefined;
  if (sessionLatest && sessionLatest.stage !== 'done' && typeof sessionLatest.fileName === 'string'
    && typeof inputPath === 'string' && baseName(inputPath) === sessionLatest.fileName) return sessionLatest;
  return { stage: 'parse', pagesDone: 0, totalPages: 0 };
}
